/**
 * MWF Attribution — storefront tracking module (Phase 1)
 *
 * Pure functions + a dependency-injected bootstrap. UMD-wrapped so it:
 *  - attaches to window.MWFAttribution and auto-inits in the browser
 *  - is require()-able in Node for unit tests (no auto-init when window is absent)
 *
 * Output: localStorage (mwf_journey, mwf_visitor_id) + Shopify cart attributes.
 *
 * NOTE: uses its own `mwf_journey` key so it stays fully isolated from the
 * legacy UTM block in analytics-tracking.js (which owns `mwf_attribution`).
 * The two run side by side; nothing is shared.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) { module.exports = api; }
  if (typeof window !== 'undefined') {
    root.MWFAttribution = api;
    try {
      if (!root.MWF_ATTRIBUTION_NO_AUTOINIT && typeof api.init === 'function') {
        api.init(api._defaultDeps());
      }
    } catch (e) { /* never break the storefront */ }
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var api = {};
  api.VERSION = '1.0.0';

  // --- constants ---
  var TRACK_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'wbraid', 'gbraid', 'fbclid', 'ttclid', 'twclid'];
  var STORAGE_KEY = 'mwf_journey';
  var VISITOR_KEY = 'mwf_visitor_id';
  var TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30-day attribution window
  var MAX_TOUCHPOINTS = 5;
  var MAX_CLICKSTREAM = 30;

  var CH = {
    GOOGLE_ADS: 'google_ads',
    META_ADS: 'meta_ads',
    TIKTOK_ADS: 'tiktok_ads',
    TWITTER_ADS: 'twitter_ads',
    ORGANIC_SEARCH: 'organic_search',
    ORGANIC_SOCIAL: 'organic_social',
    YOUTUBE: 'youtube',
    TIKTOK: 'tiktok',
    TWITTER: 'twitter',
    AI: 'ai',
    DIRECT: 'direct',
    OTHER: 'other'
  };

  // (later tasks attach api.classifyChannel,
  //  api.buildTouchpoint, api.appendTouchpoint, api.appendClickstream,
  //  api.readState, api.writeState, api.isExpired, api.ensureVisitorId,
  //  api.buildCartAttributes, api.syncToCart, api.init, api._defaultDeps)

  api.parseParams = function (search) {
    var out = {};
    try {
      var sp = new URLSearchParams(search || '');
      for (var i = 0; i < TRACK_KEYS.length; i++) {
        var k = TRACK_KEYS[i];
        var v = sp.get(k);
        if (v) { out[k] = v; }
      }
    } catch (e) { /* malformed query */ }
    return out;
  };

  api.refHost = function (referrer) {
    if (!referrer) { return ''; }
    try {
      return new URL(referrer).hostname.toLowerCase();
    } catch (e) {
      return '';
    }
  };

  // UTM values are identifiers, not prose: match the whole value or one of its
  // dot/underscore/hyphen separated tokens, never a bare substring. A substring
  // test makes short tokens dangerous — 'x' would match 'boxset', 'ig' would
  // match 'bigcommerce'.
  function utmMatch(str, list) {
    str = (str || '').toLowerCase().replace(/^\s+|\s+$/g, '');
    if (!str) { return false; }
    var parts = str.split(/[^a-z0-9]+/);
    for (var i = 0; i < list.length; i++) {
      if (str === list[i]) { return true; }
      for (var j = 0; j < parts.length; j++) {
        if (parts[j] && parts[j] === list[i]) { return true; }
      }
    }
    return false;
  }

  // Second-level suffixes that sit in front of a country TLD, so that
  // hostRoot('www.google.com.au') is 'google' rather than 'com'.
  var SLD_SUFFIXES = ['co', 'com', 'net', 'org', 'gov', 'edu', 'ac', 'or', 'ne'];

  // The registrable label of a host — the part that identifies the site.
  function hostRoot(host) {
    host = (host || '').toLowerCase().replace(/^\s+|\s+$/g, '').replace(/\.$/, '');
    var labels = host.split('.');
    if (labels.length < 2) { return host; }
    var end = labels.length - 1;                                  // drop the TLD
    if (end > 1 && SLD_SUFFIXES.indexOf(labels[end - 1]) !== -1) { end--; }
    return labels[end - 1];
  }

  // Host tests must respect label boundaries. 'www.reddit.com' CONTAINS the
  // string 't.co' (reddi|t.co|m), so a substring test filed every Reddit visit
  // under X. hostRootIs matches a site across any TLD (google.com.au,
  // google.co.uk); hostIs matches one exact domain or a subdomain of it.
  function hostRootIs(host, names) {
    var root = hostRoot(host);
    if (!root) { return false; }
    for (var i = 0; i < names.length; i++) {
      if (root === names[i]) { return true; }
    }
    return false;
  }

  function hostIs(host, domains) {
    host = (host || '').toLowerCase().replace(/^\s+|\s+$/g, '').replace(/\.$/, '');
    if (!host) { return false; }
    for (var i = 0; i < domains.length; i++) {
      var d = domains[i];
      if (host === d || host.slice(-(d.length + 1)) === '.' + d) { return true; }
    }
    return false;
  }

  var PAID_MEDIUMS = ['cpc', 'ppc', 'paid', 'paidsocial', 'paidsearch', 'display', 'cpm', 'cpv'];
  var GOOGLE_SOURCES = ['google', 'google.com', 'bing', 'youtube', 'youtube.com', 'gdn', 'adwords']; // youtube here = YouTube ads, which run through Google Ads
  var META_SOURCES = ['facebook', 'facebook.com', 'instagram', 'instagram.com', 'meta', 'fb', 'ig'];
  var YOUTUBE_SOURCES = ['youtube', 'youtube.com', 'yt', 'youtu.be'];
  var TIKTOK_SOURCES = ['tiktok', 'tiktok.com'];
  // A bare 'x' is safe here only because utmMatch compares whole tokens:
  // utm_source=x matches, utm_source=boxset does not.
  var TWITTER_SOURCES = ['twitter', 'twitter.com', 'x', 'x.com', 't.co'];
  var SOCIAL_TOKENS = ['facebook', 'facebook.com', 'instagram', 'instagram.com', 'fb', 'ig', 'pinterest', 'pinterest.com', 'linkedin', 'linkedin.com', 'reddit', 'reddit.com', 'snapchat', 'snapchat.com', 'social'];

  var AI_HOSTS = ['chatgpt.com', 'chat.openai.com', 'openai.com', 'perplexity.ai', 'gemini.google.com', 'bard.google.com', 'claude.ai', 'copilot.microsoft.com', 'poe.com'];
  var SEARCH_ROOTS = ['google', 'bing', 'yahoo', 'duckduckgo', 'ecosia', 'baidu', 'yandex', 'ask', 'aol'];
  var YOUTUBE_ROOTS = ['youtube', 'youtu'];
  var TIKTOK_ROOTS = ['tiktok'];
  var TWITTER_DOMAINS = ['twitter.com', 'x.com', 't.co'];
  var SOCIAL_ROOTS = ['facebook', 'instagram', 'pinterest', 'linkedin', 'reddit', 'snapchat', 'whatsapp', 'messenger', 'lnkd'];

  api.classifyChannel = function (params, referrerHost) {
    params = params || {};

    // 1. Click IDs (most reliable; survive iOS tracking prevention).
    if (params.gclid || params.wbraid || params.gbraid) { return CH.GOOGLE_ADS; }
    if (params.fbclid) { return CH.META_ADS; }
    if (params.ttclid) { return CH.TIKTOK_ADS; }
    if (params.twclid) { return CH.TWITTER_ADS; }

    var medium = (params.utm_medium || '').toLowerCase();
    var source = (params.utm_source || '').toLowerCase();

    // 2. Paid UTMs.
    if (utmMatch(medium, PAID_MEDIUMS)) {
      if (utmMatch(source, GOOGLE_SOURCES)) { return CH.GOOGLE_ADS; }
      if (utmMatch(source, META_SOURCES)) { return CH.META_ADS; }
      if (utmMatch(source, TIKTOK_SOURCES)) { return CH.TIKTOK_ADS; }
      if (utmMatch(source, TWITTER_SOURCES)) { return CH.TWITTER_ADS; }
      return CH.OTHER;
    }

    // 3. Organic YouTube / TikTok / X get their own channel, ahead of the
    // generic organic-social bucket below (order matters: these tokens used
    // to be part of SOCIAL_TOKENS/SOCIAL_ROOTS and would otherwise still match
    // there first).
    if (utmMatch(source, YOUTUBE_SOURCES) || utmMatch(medium, YOUTUBE_SOURCES)) { return CH.YOUTUBE; }
    if (utmMatch(source, TIKTOK_SOURCES) || utmMatch(medium, TIKTOK_SOURCES)) { return CH.TIKTOK; }
    if (utmMatch(source, TWITTER_SOURCES) || utmMatch(medium, TWITTER_SOURCES)) { return CH.TWITTER; }

    // 4. Other organic social via UTM source/medium.
    if (utmMatch(source, SOCIAL_TOKENS) || utmMatch(medium, SOCIAL_TOKENS)) {
      return CH.ORGANIC_SOCIAL;
    }

    // 5. Any other UTM-tagged traffic (email, affiliate, etc.).
    if (medium || source) { return CH.OTHER; }

    // 6. Referrer-based (order matters: AI before search, since the registrable
    // root of gemini.google.com is "google"; platform-specific hosts before the
    // generic social list).
    if (referrerHost) {
      if (hostIs(referrerHost, AI_HOSTS)) { return CH.AI; }
      if (hostRootIs(referrerHost, SEARCH_ROOTS)) { return CH.ORGANIC_SEARCH; }
      if (hostRootIs(referrerHost, YOUTUBE_ROOTS)) { return CH.YOUTUBE; }
      if (hostRootIs(referrerHost, TIKTOK_ROOTS)) { return CH.TIKTOK; }
      if (hostIs(referrerHost, TWITTER_DOMAINS)) { return CH.TWITTER; }
      if (hostRootIs(referrerHost, SOCIAL_ROOTS)) { return CH.ORGANIC_SOCIAL; }
      return CH.OTHER;
    }

    // 7. Nothing → direct.
    return CH.DIRECT;
  };

  api.buildTouchpoint = function (params, referrerHost, channel, ts) {
    params = params || {};
    var clickIds = {};
    ['gclid', 'wbraid', 'gbraid', 'fbclid', 'ttclid', 'twclid'].forEach(function (k) {
      if (params[k]) { clickIds[k] = params[k]; }
    });
    return {
      ts: ts,
      channel: channel,
      source: params.utm_source || '',
      medium: params.utm_medium || '',
      campaign: params.utm_campaign || '',
      referrer_host: referrerHost || '',
      click_ids: clickIds
    };
  };

  api.appendTouchpoint = function (path, tp, max) {
    var out = (path || []).slice();
    var last = out.length ? out[out.length - 1] : null;
    if (last && last.channel === tp.channel && last.source === tp.source && last.campaign === tp.campaign) {
      last.ts = tp.ts; // same touch re-entered; refresh recency, do not duplicate
    } else {
      out.push(tp);
    }
    if (out.length > max) { out = out.slice(out.length - max); }
    return out;
  };

  api.appendClickstream = function (stream, page, max) {
    var out = (stream || []).slice();
    if (out.length && out[out.length - 1] === page) { return out; }
    out.push(page);
    if (out.length > max) { out = out.slice(out.length - max); }
    return out;
  };

  api.readState = function (storage) {
    try {
      var raw = storage.getItem(STORAGE_KEY);
      if (!raw) { return null; }
      return JSON.parse(raw);
    } catch (e) { return null; }
  };

  api.writeState = function (storage, state) {
    try { storage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) { /* full/disabled */ }
  };

  api.isExpired = function (state, now, ttl) {
    if (!state || !state.updated) { return true; }
    return (now - state.updated) > ttl;
  };

  api.ensureVisitorId = function (storage, randomId) {
    var id;
    try { id = storage.getItem(VISITOR_KEY); } catch (e) { id = null; }
    if (!id) {
      id = randomId();
      try { storage.setItem(VISITOR_KEY, id); } catch (e) { /* ignore */ }
    }
    return id;
  };

  api.buildCartAttributes = function (state, visitorId) {
    var last = state.lastTouch || {};
    var first = state.firstTouch || {};
    return {
      mwf_visitor_id: visitorId,
      mwf_first_channel: first.channel || '',
      mwf_last_channel: last.channel || '',
      mwf_utm_source: last.source || '',
      mwf_utm_medium: last.medium || '',
      mwf_utm_campaign: last.campaign || '',
      mwf_click_ids: JSON.stringify(last.click_ids || {}),
      mwf_path: JSON.stringify(state.path || []),
      mwf_clickstream: JSON.stringify(state.clickstream || [])
    };
  };

  api.syncToCart = function (fetchFn, attrs) {
    if (!fetchFn) { return Promise.resolve(null); }
    return fetchFn('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).catch(function () { return null; });
  };

  api.init = function (deps) {
    var storage = deps.storage;
    var location = deps.location;
    var nowMs = deps.now();

    var visitorId = api.ensureVisitorId(storage, deps.randomId);
    var params = api.parseParams(location.search);
    var host = api.refHost(deps.referrer);
    var hasParams = Object.keys(params).length > 0;
    var external = !!host && host !== location.hostname;

    var prev = api.readState(storage);
    var expired = api.isExpired(prev, nowMs, TTL_MS);
    var state = (prev && !expired)
      ? prev
      : { updated: 0, firstTouch: null, lastTouch: null, path: [], clickstream: [] };

    var newArrival = !prev || expired || hasParams || external;
    if (newArrival) {
      var channel = api.classifyChannel(params, host);
      var tp = api.buildTouchpoint(params, host, channel, nowMs);
      if (!state.firstTouch) { state.firstTouch = tp; }
      state.lastTouch = tp;
      state.path = api.appendTouchpoint(state.path, tp, MAX_TOUCHPOINTS);
    }

    state.clickstream = api.appendClickstream(state.clickstream, location.pathname, MAX_CLICKSTREAM);
    state.updated = nowMs;
    api.writeState(storage, state);

    if (state.lastTouch) {
      api.syncToCart(deps.fetchFn, api.buildCartAttributes(state, visitorId));
    }
    return state;
  };

  api._defaultDeps = function () {
    return {
      storage: window.localStorage,
      location: window.location,
      referrer: document.referrer,
      now: Date.now,
      fetchFn: (typeof window.fetch === 'function') ? window.fetch.bind(window) : null,
      randomId: function () {
        if (window.crypto && typeof window.crypto.randomUUID === 'function') {
          return window.crypto.randomUUID();
        }
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
          var r = (Math.random() * 16) | 0;
          var v = (c === 'x') ? r : ((r & 0x3) | 0x8);
          return v.toString(16);
        });
      }
    };
  };

  return api;
});
